/**
 * Strict RFC 4180 CSV reader for containment uploads. Mirrors the backend
 * (containment-service UploadFileReader / Commons CSV) so the preview and
 * the submit agree:
 *
 * - UTF-8 only (a BOM is allowed and dropped); comma delimiter only.
 * - Quoted fields may hold commas, `""` escaped quotes and line breaks.
 * - A quote inside an unquoted field is kept literally; text after a
 *   closing quote, or a quote left open at end of file, is an error.
 * - CR, LF and CRLF all end a record. Values are not trimmed.
 */

export const InvalidCsvEncoding =
	"Invalid CSV file. File must be comma-delimited UTF-8 text";

export function InvalidCsvRow(row: number): string {
	return `Invalid CSV file. Row ${row} could not be read; check for unbalanced quotes or non UTF-8 characters`;
}

export function IsCsvFileName(name: string): boolean {
	return name.toLowerCase().endsWith(".csv");
}

/** Decodes bytes as UTF-8, rejecting anything else (Windows-1252, UTF-16, binary). */
export function DecodeCsv(data: Uint8Array): string {
	let text: string;
	try {
		// ignoreBOM: false (default) drops a leading UTF-8 BOM
		text = new TextDecoder("utf-8", { fatal: true }).decode(data);
	} catch {
		throw new Error(InvalidCsvEncoding);
	}
	// BOM-less UTF-16 of ASCII text is valid UTF-8 full of NULs; reject it
	// (the backend does the same) rather than previewing corrupted values.
	if (text.includes("\0")) {
		throw new Error(InvalidCsvEncoding);
	}
	return text;
}

/**
 * Lazily parses CSV text into records (arrays of raw field values) so callers
 * can stop after the preview rows. An empty line is a record with one empty
 * field, as in Commons CSV with empty lines kept.
 */
export function* IterateCsv(text: string): Generator<string[]> {
	let record: string[] = [];
	let field = "";
	let recordNumber = 1;
	let i = 0;
	const n = text.length;

	while (i < n) {
		const ch = text[i];

		if (ch === '"' && field === "") {
			// Quoted field
			i++;
			for (;;) {
				if (i >= n) {
					throw new Error(InvalidCsvRow(recordNumber));
				}
				if (text[i] === '"') {
					if (text[i + 1] === '"') {
						field += '"';
						i += 2;
						continue;
					}
					i++;
					break;
				}
				field += text[i];
				i++;
			}
			const next = text[i];
			if (i < n && next !== "," && next !== "\r" && next !== "\n") {
				throw new Error(InvalidCsvRow(recordNumber));
			}
			continue;
		}

		if (ch === ",") {
			record.push(field);
			field = "";
			i++;
		} else if (ch === "\r" || ch === "\n") {
			i += ch === "\r" && text[i + 1] === "\n" ? 2 : 1;
			record.push(field);
			yield record;
			record = [];
			field = "";
			recordNumber++;
		} else {
			field += ch;
			i++;
		}
	}

	// Last record without a trailing line break
	if (field !== "" || record.length > 0) {
		record.push(field);
		yield record;
	}
}

/** Parses the whole CSV text into records. */
export function ParseCsv(text: string): string[][] {
	return Array.from(IterateCsv(text));
}

export function IsBlankCsvRecord(record: string[]): boolean {
	return record.every((value) => value === "");
}

/** 1-based index of the first column holding a non-empty value, 0 when none. */
export function FirstUsedCsvColumn(records: string[][]): number {
	let min = 0;
	for (const record of records) {
		for (let c = 0; c < record.length; c++) {
			if (record[c] !== "") {
				const col = c + 1;
				min = min === 0 ? col : Math.min(min, col);
				break;
			}
		}
	}
	return min;
}

/** 1-based index of the last column holding a non-empty value, 0 when none. */
export function LastUsedCsvColumn(records: string[][]): number {
	return records.reduce((max, record) => {
		for (let c = record.length - 1; c >= 0; c--) {
			if (record[c] !== "") {
				return Math.max(max, c + 1);
			}
		}
		return max;
	}, 0);
}
